import { type ComponentProps, type Ref } from "react";
import { type VariantProps } from "class-variance-authority";

import { cn } from "../../lib/utils";
import { buttonGroupVariants } from "./styles";

export interface ButtonGroupProps extends ComponentProps<"div">, VariantProps<typeof buttonGroupVariants> {
  ref?: Ref<HTMLDivElement>;
  /**
   * How the grouped buttons are arranged.
   * - `joined` — buttons sit flush as one continuous control (inner corners/borders collapsed).
   * - `separated` — buttons keep their own shape with a gap between them.
   * @default "joined"
   */
  layout?: "joined" | "separated";
}

/**
 * Arranges a set of sibling `<Button>`s as a single visual group. Consumed as `<Button.Group>`.
 *
 * @example
 * ```tsx
 * <Button.Group>
 *   <Button variant="outline">Prev</Button>
 *   <Button variant="outline">Next</Button>
 * </Button.Group>
 *
 * <Button.Group layout="separated">
 *   <Button>Save</Button>
 *   <Button variant="ghost">Cancel</Button>
 * </Button.Group>
 * ```
 */
export const ButtonGroup = ({ className, layout, ref, ...props }: ButtonGroupProps) => (
  <div
    ref={ref}
    role="group"
    data-slot="button-group"
    className={cn(buttonGroupVariants({ layout }), className)}
    {...props}
  />
);
